import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Badge,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from "@mui/material";
import { DataGrid, GridToolbar, GridActionsCellItem } from "@mui/x-data-grid";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  LockOpen as UnblockIcon,
} from "@mui/icons-material";
import PersonIcon from "@mui/icons-material/Person";
import { useNavigate } from "react-router-dom";
import { AdminDataContext } from "../../../useContext/AdminContext";
import API from "../../../api";
import moment from "moment";

const UserList = () => {
  const { snackbar, adminData } = useContext(AdminDataContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  // State management
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);

  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Fetch users from API
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await API.get("/api/v1/users/all-users");
      if (response.data.success) {
        const usersWithId = response.data.data.map((user) => ({
          id: user._id,
          ...user,
        }));
        setUsers(usersWithId);
        setFilteredUsers(usersWithId);
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || "Failed to fetch users";
      snackbar(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchUsers();
  }, []);

  // Handle search
  useEffect(() => {
    if (search.trim() === "") {
      setFilteredUsers(users);
    } else {
      const searchLower = search.toLowerCase();
      const filtered = users.filter(
        (user) =>
          user.fullName?.toLowerCase().includes(searchLower) ||
          user.email?.toLowerCase().includes(searchLower) ||
          user.phone?.toLowerCase().includes(searchLower) ||
          user.userType?.toLowerCase().includes(searchLower)
      );
      setFilteredUsers(filtered);
    }
  }, [search, users]);

  // Handle refresh
  const handleRefresh = () => {
    setSearch("");
    fetchUsers();
  };

  // Clear search
  const handleClearSearch = () => {
    setSearch("");
  };

  // Get user type color
  const getUserTypeColor = (userType) => {
    switch (userType) {
      case "SUPER_ADMIN":
        return "error";
      case "ADMIN":
        return "warning";
      default:
        return "default";
    }
  };

  // Check if user is currently logged in
  const isCurrentUser = (user) => {
    return (
      adminData?.email === user.email ||
      adminData?._id === user._id ||
      adminData?.id === user._id
    );
  };

  // Handle delete user
  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      setActionLoading(true);
      const response = await API.delete(
        `/api/v1/users/delete-user/${selectedUser._id}`
      );

      if (response.data.success) {
        snackbar("User deleted successfully", "success");
        setDeleteDialogOpen(false);
        setSelectedUser(null);
        fetchUsers();
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || "Failed to delete user";
      snackbar(errorMessage, "error");
    } finally {
      setActionLoading(false);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setSelectedUser(null);
  };

  // Handle block/unblock user
  const handleToggleBlock = (user) => {
    setSelectedUser(user);
    setBlockDialogOpen(true);
  };

  const confirmToggleBlock = async () => {
    if (!selectedUser) return;

    try {
      setActionLoading(true);
      const response = await API.put(
        `/api/v1/users/toggle-block/${selectedUser._id}`
      );

      if (response.data.success) {
        const action = selectedUser.isBlocked ? "unblocked" : "blocked";
        snackbar(`User ${action} successfully`, "success");
        setBlockDialogOpen(false);
        setSelectedUser(null);
        fetchUsers();
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || "Failed to update user status";
      snackbar(errorMessage, "error");
    } finally {
      setActionLoading(false);
    }
  };

  const cancelToggleBlock = () => {
    setBlockDialogOpen(false);
    setSelectedUser(null);
  };

  // DataGrid columns
  const columns = [
    {
      field: "fullName",
      headerName: "Full Name",
      minWidth: 180,
      flex: 1,
      renderCell: (params) => {
        const currentUser = isCurrentUser(params.row);
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {currentUser ? (
              <Badge
                color="success"
                variant="dot"
                sx={{
                  "& .MuiBadge-badge": {
                    animation: "pulse 2s infinite",
                  },
                  "@keyframes pulse": {
                    "0%": {
                      boxShadow: "0 0 0 0 rgba(76, 175, 80, 0.7)",
                    },
                    "70%": {
                      boxShadow: "0 0 0 6px rgba(76, 175, 80, 0)",
                    },
                    "100%": {
                      boxShadow: "0 0 0 0 rgba(76, 175, 80, 0)",
                    },
                  },
                }}
              >
                <PersonIcon sx={{ color: "#4CAF50", fontSize: 20 }} />
              </Badge>
            ) : null}
            <Typography
              variant="body2"
              sx={{
                fontWeight: currentUser ? 600 : 500,
                color: currentUser ? "#4CAF50" : "#333",
              }}
            >
              {params.value || "N/A"}
            </Typography>
            {currentUser && (
              <Chip
                label="You"
                size="small"
                color="success"
                sx={{
                  height: 20,
                  fontSize: "0.65rem",
                  fontWeight: 600,
                }}
              />
            )}
          </Box>
        );
      },
    },
    {
      field: "email",
      headerName: "Email",
      minWidth: 200,
      flex: 1,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            color: "#666",
          }}
        >
          {params.value || "N/A"}
        </Typography>
      ),
    },
    {
      field: "phone",
      headerName: "Phone",
      minWidth: 130,
      flex: 0.8,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            color: "#666",
          }}
        >
          {params.value || "N/A"}
        </Typography>
      ),
    },
    {
      field: "userType",
      headerName: "User Type",
      minWidth: 140,
      flex: 0.8,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
          <Chip
            label={params.value || "N/A"}
            color={getUserTypeColor(params.value)}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: "0.75rem",
            }}
          />
          {params.row.isBlocked && (
            <Chip
              label="Blocked"
              size="small"
              color="error"
              sx={{
                fontWeight: 600,
                fontSize: "0.65rem",
                height: 20,
              }}
            />
          )}
        </Box>
      ),
    },
    {
      field: "createdAt",
      headerName: "Created At",
      minWidth: 160,
      flex: 1,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            color: "#666",
          }}
        >
          {params.value
            ? moment(params.value).format("MMM DD, YYYY HH:mm")
            : "N/A"}
        </Typography>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      type: "actions",
      getActions: (params) => {
        const isSuperAdmin = params.row.userType === "SUPER_ADMIN";
        const actions = [];

        if (!isSuperAdmin) {
          // Block/Unblock button
          actions.push(
            <GridActionsCellItem
              icon={
                <Tooltip
                  title={params.row.isBlocked ? "Unblock User" : "Block User"}
                >
                  {params.row.isBlocked ? <UnblockIcon /> : <BlockIcon />}
                </Tooltip>
              }
              label={params.row.isBlocked ? "Unblock" : "Block"}
              onClick={() => handleToggleBlock(params.row)}
              sx={{
                color: params.row.isBlocked ? "#4CAF50" : "#FF9800",
              }}
            />
          );

          // Delete button
          actions.push(
            <GridActionsCellItem
              icon={
                <Tooltip title="Delete User">
                  <DeleteIcon />
                </Tooltip>
              }
              label="Delete"
              onClick={() => handleDeleteUser(params.row)}
              sx={{
                color: "#f44336",
              }}
            />
          );
        }

        return actions;
      },
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 2,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontSize: { xs: "1.5rem", md: "1.75rem" },
            color: "#333",
            fontWeight: 600,
          }}
        >
          User List
        </Typography>

        {/* Add User Button, Search and Refresh */}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            width: { xs: "100%", sm: "auto" },
            alignItems: "center",
          }}
        >
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/add-user")}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              px: 2,
              background: "linear-gradient(135deg, #42A5F5 0%, #1565C0 100%)",
              boxShadow: "0 4px 15px rgba(66, 165, 245, 0.4)",
              transition: "all 0.3s ease",
              "&:hover": {
                background: "linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)",
                boxShadow: "0 6px 20px rgba(66, 165, 245, 0.6)",
                transform: "translateY(-2px)",
              },
              "&:active": {
                transform: "translateY(0)",
              },
            }}
          >
            Add User
          </Button>
          <TextField
            placeholder="Search users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{
              width: { xs: "100%", sm: 300 },
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#fff",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#999" }} />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    edge="end"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <IconButton
            onClick={handleRefresh}
            disabled={loading}
            sx={{
              backgroundColor: "primary.main",
              color: "#fff",
              "&:hover": {
                backgroundColor: "primary.dark",
              },
              "&:disabled": {
                backgroundColor: "#ccc",
              },
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* User Count */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ color: "#666" }}>
          Total Users: <strong>{filteredUsers.length}</strong>
          {search && (
            <span>
              {" "}
              (filtered from <strong>{users.length}</strong>)
            </span>
          )}
        </Typography>
      </Box>

      {/* DataGrid */}
      <Paper
        sx={{
          width: "100%",
          backgroundColor: "#ffffff",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
          borderRadius: 2,
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 400,
            }}
          >
            <CircularProgress />
          </Box>
        ) : filteredUsers.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 400,
              p: 3,
            }}
          >
            <Alert severity="info" sx={{ width: "100%", maxWidth: 500 }}>
              {search
                ? `No users found matching "${search}"`
                : "No users available"}
            </Alert>
          </Box>
        ) : (
          <DataGrid
            rowHeight={43}
            rows={filteredUsers}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10, page: 0 },
              },
            }}
            pageSizeOptions={[5, 10, 25, 50, 100]}
            disableRowSelectionOnClick
            autoHeight
            slots={{
              toolbar: GridToolbar,
            }}
            slotProps={{
              toolbar: {
                showQuickFilter: false,
                printOptions: { disableToolbarButton: true },
              },
            }}
            sx={{
              border: "none",
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #f0f0f0",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#fafafa",
                borderBottom: "2px solid #e0e0e0",
                fontWeight: 600,
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 600,
                color: "#333",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#f5f5f5",
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "2px solid #e0e0e0",
                backgroundColor: "#fafafa",
                display: "none",
              },
            }}
          />
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            color: theme.palette.error.main,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <DeleteIcon />
          Confirm User Deletion
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. The user will be permanently deleted.
          </Alert>
          {selectedUser && (
            <Box>
              <Typography variant="body1" gutterBottom>
                Are you sure you want to delete this user?
              </Typography>
              <Box sx={{ mt: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Name:</strong> {selectedUser.fullName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Email:</strong> {selectedUser.email}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>User Type:</strong> {selectedUser.userType}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={cancelDelete}
            disabled={actionLoading}
            variant="outlined"
            sx={{
              borderRadius: "8px",
              textTransform: "none",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteUser}
            disabled={actionLoading}
            variant="contained"
            color="error"
            sx={{
              borderRadius: "8px",
              textTransform: "none",
            }}
          >
            {actionLoading ? <CircularProgress size={24} /> : "Delete User"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Block/Unblock Confirmation Dialog */}
      <Dialog
        open={blockDialogOpen}
        onClose={cancelToggleBlock}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            color: selectedUser?.isBlocked ? "#4CAF50" : "#FF9800",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          {selectedUser?.isBlocked ? <UnblockIcon /> : <BlockIcon />}
          {selectedUser?.isBlocked ? "Unblock User" : "Block User"}
        </DialogTitle>
        <DialogContent>
          <Alert
            severity={selectedUser?.isBlocked ? "info" : "warning"}
            sx={{ mb: 2 }}
          >
            {selectedUser?.isBlocked
              ? "The user will be able to access the system again."
              : "The user will not be able to access the system."}
          </Alert>
          {selectedUser && (
            <Box>
              <Typography variant="body1" gutterBottom>
                Are you sure you want to{" "}
                {selectedUser.isBlocked ? "unblock" : "block"} this user?
              </Typography>
              <Box sx={{ mt: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Name:</strong> {selectedUser.fullName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Email:</strong> {selectedUser.email}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>User Type:</strong> {selectedUser.userType}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Current Status:</strong>{" "}
                  <Chip
                    label={selectedUser.isBlocked ? "Blocked" : "Active"}
                    size="small"
                    color={selectedUser.isBlocked ? "error" : "success"}
                    sx={{ fontSize: "0.7rem" }}
                  />
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={cancelToggleBlock}
            disabled={actionLoading}
            variant="outlined"
            sx={{
              borderRadius: "8px",
              textTransform: "none",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmToggleBlock}
            disabled={actionLoading}
            variant="contained"
            color={selectedUser?.isBlocked ? "success" : "warning"}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
            }}
          >
            {actionLoading ? (
              <CircularProgress size={24} />
            ) : selectedUser?.isBlocked ? (
              "Unblock User"
            ) : (
              "Block User"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserList;
